import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';/**
 * Shared balance cache + invalidation bus.
 *
 * Goal: any write action (log income, save plan, save actual balances, rebalance)
 * must immediately refresh every view that reads balances (Dashboard, Accounts,
 * Rebalance) without manual refresh or stale state.
 *
 * This is a tiny, dependency-free replacement for React Query for this one domain.
 */

const cache = new Map();           // key → { data, fetchedAt }
const subscribers = new Set();     // set of callback functions
const FIVE_MIN_MS = 5 * 60 * 1000; // soft TTL — re-fetch if older than this

function notifySubscribers(payload = {}) {
  subscribers.forEach((fn) => {
    try { fn(payload); } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[useAccountBalances] subscriber threw during notify:', err);
      }
    }
  });
}

// --- Sync status bus ---------------------------------------------------
// Tracks in-flight write mutations so the Dashboard can show a subtle
// real-time status pill. Call `beginSync()` before a write and
// `endSync({ success })` after it resolves. `useSyncStatus()` subscribes
// any component (currently the Dashboard header pill) to the live status.
//
// States: 'idle' | 'syncing' | 'synced' | 'error'
//   - 'syncing' while ≥1 mutation is in-flight
//   - 'synced' for ~1.5s after last mutation succeeds, then → 'idle'
//   - 'error'  for ~3s after a mutation fails, then → 'idle'

let inFlight = 0;
let syncStatus = 'idle';
let lastLabel = null;
let autoHideTimer = null;
const syncSubscribers = new Set();

function emitSyncStatus() {
  syncSubscribers.forEach((fn) => {
    try { fn({ status: syncStatus, label: lastLabel }); } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[useAccountBalances] sync subscriber threw:', err);
      }
    }
  });
}

function scheduleAutoHide(ms) {
  if (autoHideTimer) clearTimeout(autoHideTimer);
  autoHideTimer = setTimeout(() => {
    // Only revert to idle if no new mutation started in the meantime
    if (inFlight === 0 && (syncStatus === 'synced' || syncStatus === 'error')) {
      syncStatus = 'idle';
      lastLabel = null;
      emitSyncStatus();
    }
  }, ms);
}

export function beginSync(label = null) {
  inFlight += 1;
  syncStatus = 'syncing';
  lastLabel = label;
  if (autoHideTimer) { clearTimeout(autoHideTimer); autoHideTimer = null; }
  emitSyncStatus();
}

export function endSync({ success = true } = {}) {
  inFlight = Math.max(0, inFlight - 1);
  if (inFlight > 0) return; // still other mutations running
  syncStatus = success ? 'synced' : 'error';
  emitSyncStatus();
  scheduleAutoHide(success ? 1500 : 3000);
}

/**
 * Wrap an async function so the Sync Status pill reflects its lifecycle
 * automatically. Returns whatever `fn()` returns and re-throws on error
 * (after marking sync as failed).
 *
 *   const res = await trackSync(() => axios.post(...), 'Saving plan');
 */
export async function trackSync(fn, label = null) {
  beginSync(label);
  try {
    const result = await fn();
    endSync({ success: true });
    return result;
  } catch (e) {
    endSync({ success: false });
    throw e;
  }
}

/**
 * Subscribe a callback to balance-invalidation events. Call this from any
 * component that reads balances via its own fetch (not via `useAccountBalances`)
 * so it can also refresh on invalidation.
 * Returns an unsubscribe function.
 */
export function subscribeToBalanceInvalidation(callback) {
  subscribers.add(callback);
  return () => subscribers.delete(callback);
}

/**
 * Read the current cached balances for a year without triggering a fetch.
 * Useful for components that maintain their own view-model (e.g. Dashboard
 * holds a full summary object) and just need to patch the embedded balances
 * when an optimistic update happens.
 */
export function getCachedBalances(year) {
  const entry = cache.get(`balances:${year}`);
  return entry ? entry.data : null;
}
export function getCachedSummary(year) {
  const entry = cache.get(`summary:${year}`);
  return entry ? entry.data : null;
}

/**
 * Apply a delta to every cached balance entry, IN PLACE, and notify subscribers
 * so Dashboard/Accounts/Rebalance refresh optimistically without waiting for the
 * network. Returns a snapshot you MUST pass to `restoreOptimisticSnapshot()` on
 * error to undo.
 *
 * @param {Object} delta - e.g. { operations: 550, business_growth: 250, taxes: 197.83, buffer: 2.17 }
 */
export function applyOptimisticBalanceDelta(delta) {
  // Deep-snapshot current cache entries so we can roll back atomically
  const snapshot = new Map();
  cache.forEach((entry, key) => {
    snapshot.set(key, JSON.parse(JSON.stringify(entry)));
  });

  // Mutate cached balances:<year> entries
  cache.forEach((entry, key) => {
    if (!entry || !entry.data) return;
    if (key.startsWith('balances:')) {
      ['operations', 'business_growth', 'taxes', 'buffer'].forEach((k) => {
        if (delta[k] !== undefined) {
          entry.data[k] = Number(((entry.data[k] || 0) + delta[k]).toFixed(2));
        }
      });
    } else if (key.startsWith('summary:') && entry.data?.current_balances) {
      const cb = entry.data.current_balances;
      ['operations', 'business_growth', 'taxes', 'buffer'].forEach((k) => {
        if (delta[k] !== undefined) {
          cb[k] = Number(((cb[k] || 0) + delta[k]).toFixed(2));
        }
      });
    }
  });

  notifySubscribers({ type: 'optimistic', delta }); // triggers hook re-render for subscribed views
  return snapshot;
}

/**
 * Restore a snapshot previously taken by `applyOptimisticBalanceDelta`.
 * Use this on request failure to cleanly revert the UI.
 */
export function restoreOptimisticSnapshot(snapshot) {
  if (!snapshot) return;
  cache.clear();
  snapshot.forEach((entry, key) => cache.set(key, entry));
  notifySubscribers({ type: 'rollback' });
}

/**
 * Clear the balance cache and re-fetch every active subscriber immediately.
 * Call this after ANY write that could change balances:
 *   - POST/DELETE /income/weekly
 *   - POST/DELETE /income/monthly
 *   - POST/DELETE /expenses
 *   - POST /financial-plan
 *   - PUT /settings/actual-balances
 *   - Rebalance execution (when it becomes a real endpoint)
 */
export function invalidateAccountBalances() {
  cache.clear();
  notifySubscribers();
}

/**
 * Low-level shared-cache hook used by the domain-specific hooks below.
 * Returns { data, loading, error, refetch } and auto-subscribes to global invalidation.
 */
function useCached(key, fetcher, deps) {
  const cached = cache.get(key);
  const [data, setData]       = useState(cached ? cached.data : null);
  const [loading, setLoading] = useState(!cached);
  const [error, setError]     = useState(null);
  const latestFetcher         = useRef(fetcher);
  latestFetcher.current       = fetcher;

  const refetch = useCallback(async () => {
    try {
      setLoading(true);
      const result = await latestFetcher.current();
      cache.set(key, { data: result, fetchedAt: Date.now() });
      setData(result);
      setError(null);
      return result;
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    // Fetch if not cached OR cached but stale
    const entry = cache.get(key);
    if (!entry || Date.now() - entry.fetchedAt > FIVE_MIN_MS) {
      refetch().catch(() => { /* surfaced via `error` */ });
    } else {
      setData(entry.data);
      setLoading(false);
    }
    const onInvalidate = (payload = {}) => {
      // For optimistic mutations / rollbacks, the cache already holds the new
      // values — just re-read, no network hit. For true invalidation, refetch.
      if (payload.type === 'optimistic' || payload.type === 'rollback') {
        const entry = cache.get(key);
        if (entry) setData(entry.data);
        return;
      }
      refetch().catch(() => { /* surfaced via `error` */ });
    };
    subscribers.add(onInvalidate);
    return () => subscribers.delete(onInvalidate);
    // Intentional: `refetch` is stable per `key` (via useCallback([key])), so
    // including it here would recreate the effect every render without changing
    // behaviour. `subscribers` is a module-level Set, not a dependency.
    // eslint-disable-next-line
  }, deps || [key]);

  return { data, loading, error, refetch };
}

/**
 * Account balances for a given year (source of truth for Personal / Business /
 * Taxes / Savings). Used by the Accounts page and indirectly by the Dashboard
 * via the summary endpoint (which embeds the same numbers server-side).
 */
export function useAccountBalances({ year }) {
  return useCached(
    `balances:${year}`,
    async () => {
      const res = await axios.get(`${API}/accounts/balances`, { params: { year } });
      return res.data;
    },
    [year]
  );
}

/**
 * Dashboard summary (includes embedded current_balances). Shares the same
 * invalidation bus so a balance mutation refreshes the Dashboard too.
 */
export function useDashboardSummary({ year }) {
  return useCached(
    `summary:${year}`,
    async () => {
      const res = await axios.get(`${API}/dashboard/summary`, { params: { year } });
      return res.data;
    },
    [year]
  );
}


/**
 * Subscribe a component to the sync-status bus.
 * Returns the current `{ status, label }` object and auto-updates on change.
 */
export function useSyncStatus() {
  const [state, setState] = useState({ status: syncStatus, label: lastLabel });
  useEffect(() => {
    const listener = (next) => setState(next);
    syncSubscribers.add(listener);
    // Emit current state immediately in case it changed between render and subscribe
    listener({ status: syncStatus, label: lastLabel });
    return () => syncSubscribers.delete(listener);
    // Mount-only subscription: `syncSubscribers` / `syncStatus` / `lastLabel` are
    // module-level bus state, not reactive values. Re-running on every render
    // would churn the subscriber set without changing behaviour.
    // eslint-disable-next-line
  }, []);
  return state;
}
